import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Scale as ScaleBase,
  MessageSquare as MessageSquareBase,
  ArrowRight as ArrowRightBase,
  Loader as LoaderBase,
  LogOut as LogOutBase,
} from '../../utils/icons';
import { companionApi } from '../../api/facades';
import { useAuth } from '../../context/AuthContext';
import { useCompanionConsent } from '../../hooks/useCompanionConsent';
import CompanionConsentModal from '../../components/Companion/CompanionConsentModal';

type IconComponent = React.ComponentType<{ className?: string }>;
const Scale = ScaleBase as IconComponent;
const MessageSquare = MessageSquareBase as IconComponent;
const ArrowRight = ArrowRightBase as IconComponent;
const Loader = LoaderBase as IconComponent;
const LogOut = LogOutBase as IconComponent;

const HubPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { showModal, requestConsent, onAccept, onDecline } = useCompanionConsent();
  const [isWorking, setIsWorking] = React.useState(false);

  const companionLinked = Number(user?.companion_id ?? 0) > 0;
  const displayName = user?.people?.first_name || user?.name || user?.email || 'there';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const doOpen = async () => {
    setIsWorking(true);
    try {
      const res = await companionApi.ssoUrl();
      const url = res?.data?.data?.url;
      if (!url) throw new Error(res?.data?.message || 'Could not open ClickCompanion.');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      toast.error(e?.message || 'Could not open ClickCompanion.');
    } finally {
      setIsWorking(false);
    }
  };

  const doLinkThenOpen = async () => {
    setIsWorking(true);
    try {
      await companionApi.link();
      await refreshUser();
      toast.success('ClickCompanion linked successfully!');
      const res = await companionApi.ssoUrl();
      const url = res?.data?.data?.url;
      if (!url) throw new Error('Could not open ClickCompanion.');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to open ClickCompanion.');
    } finally {
      setIsWorking(false);
    }
  };

  const handleCompanionClick = () => {
    if (companionLinked) {
      requestConsent(doOpen);
    } else {
      requestConsent(doLinkThenOpen);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-900">
      <CompanionConsentModal open={showModal} onAccept={onAccept} onDecline={onDecline} />

      {/* Minimal top bar */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <span className="text-lg font-bold text-primary dark:text-primary-light tracking-tight">
          ClickLaw
        </span>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-500 dark:text-slate-400 hidden sm:block">
            Hi, {displayName}
          </span>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-danger dark:text-slate-400 dark:hover:text-danger transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100">
            Welcome back, {displayName}
          </h1>
          <p className="mt-3 text-slate-500 dark:text-slate-400 text-base max-w-md mx-auto">
            Choose what you would like to do today. Both tools are here to help you practice law more efficiently.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
          {/* Case Management */}
          <div className="flex flex-col rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:border-primary/40 dark:hover:border-primary/40 transition-all p-8">
            <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 dark:bg-primary/20 mb-6">
              <Scale className="w-8 h-8 text-primary dark:text-primary-light" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              Case Management
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed flex-1">
              Manage your court matters, clients, invoices, appointments, and documents, all in one organised dashboard.
            </p>
            <button
              onClick={() => navigate('/dashboard/overview')}
              className="mt-8 inline-flex items-center justify-center gap-2 w-full bg-primary text-white px-5 py-3 rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* ClickCompanion */}
          <div className="flex flex-col rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:border-primary/40 dark:hover:border-primary/40 transition-all p-8">
            <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 dark:bg-primary/20 mb-6">
              <MessageSquare className="w-8 h-8 text-primary dark:text-primary-light" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              ClickCompanion
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed flex-1">
              Your AI legal research assistant. Ask questions and get intelligent support.
            </p>
            {!companionLinked && (
              <p className="mt-3 text-xs text-amber-600 dark:text-amber-400 font-medium">
                Not yet linked. we willll connect your account automatically when you click below.
              </p>
            )}
            <button
              onClick={handleCompanionClick}
              disabled={isWorking}
              className="mt-8 inline-flex items-center justify-center gap-2 w-full bg-primary text-white px-5 py-3 rounded-xl font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {isWorking ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  {companionLinked ? 'Opening…' : 'Linking & Opening…'}
                </>
              ) : (
                <>
                  Open ClickCompanion
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </main>

      <footer className="py-4 text-center text-xs text-slate-400 dark:text-slate-600">
        &copy; {new Date().getFullYear()} ClickLaw. All rights reserved.
      </footer>
    </div>
  );
};

export default HubPage;
